import { LinkProps } from "@tanstack/react-router";

// Route params are always strings in the URL; ids in our data are sometimes numeric.
const s = (id: string | number) => String(id);

// Identifies the profile subtree a relationship table is nested under, so its
// links resolve to the matching relationship route.
export type SupplierContext = { supplierId: string | number };
export type BuyerContext = { buyerId: string | number };

/**
 * Single source of truth for in-app navigation targets.
 *
 * Each entry is a TanStack Router navigation target (`to` + optional `params`),
 * validated against the generated route tree via `satisfies LinkProps` — a typo
 * or a path/param that isn't in the route tree is a compile error here. Spread an
 * entry into a router-aware link/button to get end-to-end type-safety:
 *
 *   <RouterTextLink {...appRoutes.leadDetail(lead.id)} />
 *   <Button as="a" {...appRoutes.subscription} />
 *
 * Catch-all (`/$`) sub-paths (e.g. supplier awards, buyer activity) are encoded
 * once here with `_splat` so call sites don't deal with the splat param.
 */
export const appRoutes = {
  home: { to: "/" } satisfies LinkProps,

  // Leads
  leads: { to: "/leads" } satisfies LinkProps,
  leadDetail: (id: string | number) =>
    ({ to: "/leads/$id", params: { id: s(id) } }) satisfies LinkProps,

  // Bids
  bids: { to: "/bids" } satisfies LinkProps,
  bidDetail: (id: string | number) =>
    ({ to: "/bids/$id", params: { id: s(id) } }) satisfies LinkProps,

  // Buyers
  buyers: { to: "/buyers" } satisfies LinkProps,
  buyersAll: { to: "/buyers/all-buyers" } satisfies LinkProps,
  buyerLists: { to: "/buyers/lists" } satisfies LinkProps,
  buyerDetail: (id: string | number) =>
    ({ to: "/buyers/$id", params: { id: s(id) } }) satisfies LinkProps,

  // Suppliers
  suppliers: { to: "/suppliers" } satisfies LinkProps,
  suppliersSearch: { to: "/suppliers/search" } satisfies LinkProps,
  supplierDetail: (id: string | number) =>
    ({ to: "/suppliers/$id", params: { id: s(id) } }) satisfies LinkProps,
  supplierAwards: (id: string | number) =>
    ({ to: "/suppliers/$id/$", params: { id: s(id), _splat: "awards" } }) satisfies LinkProps,

  // Buyer <> supplier relationship sub-pages. `context` says which side's
  // profile we're nested under, so the link resolves to the right route.
  supplierBuyer: (context: SupplierContext, buyerGuid: string | number) =>
    ({
      to: "/suppliers/$id/buyers/$buyerGuid",
      params: { id: s(context.supplierId), buyerGuid: s(buyerGuid) },
    }) satisfies LinkProps,
  buyerSupplier: (context: BuyerContext, supplierGuid: string | number) =>
    ({
      to: "/buyers/$id/supplier/$supplierGuid",
      params: { id: s(context.buyerId), supplierGuid: s(supplierGuid) },
    }) satisfies LinkProps,

  // Frameworks
  frameworks: { to: "/frameworks" } satisfies LinkProps,
  frameworkDetail: (framework_id: string | number) =>
    ({
      to: "/frameworks/$framework_id",
      params: { framework_id: s(framework_id) },
    }) satisfies LinkProps,
  frameworkSuppliers: (framework_id: string | number) =>
    ({
      to: "/frameworks/$framework_id/suppliers",
      params: { framework_id: s(framework_id) },
    }) satisfies LinkProps,
  frameworkCallOffs: (framework_id: string | number) =>
    ({
      to: "/frameworks/$framework_id/call-offs",
      params: { framework_id: s(framework_id) },
    }) satisfies LinkProps,

  // Notices / documents / contacts
  notices: { to: "/notices" } satisfies LinkProps,
  noticeDetail: (guid: string) => ({ to: "/notices/$guid", params: { guid } }) satisfies LinkProps,
  noticesV2: { to: "/noticesv2" } satisfies LinkProps,
  documents: { to: "/documents" } satisfies LinkProps,
  documentDetail: (id: string | number) =>
    ({ to: "/documents/$id", params: { id: s(id) } }) satisfies LinkProps,
  contacts: { to: "/contacts" } satisfies LinkProps,

  // Reports
  reportDetail: (reportId: string | number) =>
    ({ to: "/reports/$reportId", params: { reportId: s(reportId) } }) satisfies LinkProps,

  // Search / spend / content library
  savedSearches: { to: "/search/saved" } satisfies LinkProps,
  spend: { to: "/spend" } satisfies LinkProps,
  contentLibrary: { to: "/content-library" } satisfies LinkProps,

  // Account management
  subscription: { to: "/account-management/subscription" } satisfies LinkProps,
  feedSettings: { to: "/account-management/feed-settings" } satisfies LinkProps,
  myTeam: { to: "/account-management/my-team" } satisfies LinkProps,
  integrations: { to: "/account-management/integrations" } satisfies LinkProps,
  integrationFieldMapping: {
    to: "/account-management/integrations/field-mapping",
  } satisfies LinkProps,
} as const;
